import React, { useState, useEffect } from "react";
import {
  Badge, Box, Button, Flex, Heading, HoverCard, HStack, Image, Input,
  Portal, SimpleGrid, Table, Tabs, Text, Textarea, VStack,
} from "@chakra-ui/react";
import { ChevronDown, ChevronUp } from "lucide-react";
import axios from "axios";
import Cookies from "js-cookie";
const API_BASE_URL = process.env.REACT_APP_API_URL;

// ISO date string, comparable with the API's endDate strings
const todayStr = new Date().toISOString().slice(0, 10);
const in30DaysStr = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);

const AGREEMENT_PALETTE = {
  Active: "green",
  Created: "blue",
  Finished: "gray",
  EndingSoon: "orange",
};

const INSTRUMENT_PALETTE = {
  Available: "green",
  Rented: "blue",
  Reserved: "orange",
  AgreementInProgress: "orange",
};

const headerStyles = {
  fontFamily: "space",
  fontSize: "10px",
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: "gray.500",
};

const darkButtonStyles = {
  bg: "#09090b",
  color: "white",
  borderRadius: "sm",
  fontFamily: "space",
  fontSize: "10px",
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  _hover: { bg: "gray.800" },
};

const outlineButtonStyles = {
  variant: "outline",
  borderColor: "gray.300",
  color: "gray.700",
  borderRadius: "sm",
  fontFamily: "space",
  fontSize: "10px",
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  _hover: { bg: "gray.100" },
};

const inputStyles = {
  bg: "white",
  borderColor: "gray.300",
  color: "gray.900",
  _placeholder: { color: "gray.400" },
  _focusVisible: { borderColor: "gray.900", boxShadow: "none" },
};

const StatusBadge = ({ palette, children }) => (
  <Badge
    colorPalette={palette}
    variant="subtle"
    fontFamily="space"
    fontSize="10px"
    letterSpacing="0.15em"
    textTransform="uppercase"
    px={2}
    py={0.5}
    borderRadius="sm"
  >
    {children}
  </Badge>
);

const HoverRow = ({ label, value }) =>
  value === null || value === undefined || value === "" ? null : (
    <Flex justify="space-between" gap={4} py={0.5}>
      <Text fontSize="xs" color="gray.500">{label}</Text>
      <Text fontSize="xs" color="gray.800" fontWeight="medium" textAlign="right">{value}</Text>
    </Flex>
  );

// Rich hover card wrapper — trigger is the visible cell content
const InfoHover = ({ trigger, children }) => (
  <HoverCard.Root openDelay={250} closeDelay={100} positioning={{ placement: "right" }}>
    <HoverCard.Trigger asChild>
      <Box as="span" cursor="help" borderBottomWidth="1px" borderStyle="dotted" borderColor="gray.400">
        {trigger}
      </Box>
    </HoverCard.Trigger>
    <Portal>
      <HoverCard.Positioner>
        <HoverCard.Content
          bg="white"
          borderWidth="1px"
          borderColor="gray.200"
          borderRadius="lg"
          boxShadow="lg"
          p={4}
          minW="260px"
          maxW="320px"
          zIndex={1500}
        >
          {children}
        </HoverCard.Content>
      </HoverCard.Positioner>
    </Portal>
  </HoverCard.Root>
);

const InstrumentHoverContent = ({ inst }) => (
  <>
    {inst.thumbnail && (
      <Box bg="gray.100" borderRadius="md" mb={3} h="110px" display="flex" alignItems="center" justifyContent="center" overflow="hidden">
        <Image src={`${API_BASE_URL}${inst.thumbnail}`} alt={`${inst.brand} ${inst.model}`} objectFit="contain" h="100%" />
      </Box>
    )}
    <Text fontFamily="space" fontSize="10px" letterSpacing="0.2em" textTransform="uppercase" color="gray.500">
      {inst.brand}
    </Text>
    <Text fontFamily="heading" fontWeight="600" fontSize="lg" color="gray.900" mb={2}>
      {inst.model}
    </Text>
    <HoverRow label="Serial" value={inst.serial} />
    <HoverRow label="Color" value={inst.color} />
    <HoverRow label="Keys" value={inst.keys} />
    <HoverRow label="Basses" value={inst.basses} />
    <HoverRow label="Weight" value={inst.weight ? `${inst.weight} kg` : null} />
    <HoverRow label="Price level" value={inst.price_level} />
    <HoverRow label="Base rate" value={inst.rate ? `${inst.rate} €/month` : null} />
  </>
);

const ClientHoverContent = ({ user }) => {
  const address = [
    [user.street, user.house].filter(Boolean).join(" ") + (user.apartment ? `-${user.apartment}` : ""),
    user.settlement,
    user.municipality,
    user.province,
    user.postal_code,
    user.country,
  ].filter(Boolean).join(", ");
  return (
    <>
      <Text fontFamily="heading" fontWeight="600" fontSize="lg" color="gray.900" mb={2}>
        {user.name || `${user.firstName} ${user.lastName}`}
      </Text>
      <HoverRow label="Email" value={user.email} />
      <HoverRow label="Phone" value={user.phone} />
      {address && (
        <Box py={0.5}>
          <Text fontSize="xs" color="gray.500">Address</Text>
          <Text fontSize="xs" color="gray.800" fontWeight="medium">{address}</Text>
        </Box>
      )}
      <HoverRow label="School / institution" value={user.institution} />
      <HoverRow label="Teacher" value={user.teacher} />
      <HoverRow label="Contract language" value={user.language} />
      <HoverRow label="Agreement" value={user.agreementId ? `#${user.agreementId} (${user.agreementStatus})` : null} />
      <HoverRow label="Rented since" value={user.agreementStart} />
    </>
  );
};

// Local-state textarea + send button: typing re-renders only this component,
// not the whole agreements table.
const EmailBox = React.memo(({ agreementId, onSend }) => {
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!message.trim()) {
      alert("Please provide a message to send.");
      return;
    }
    setSending(true);
    const ok = await onSend(agreementId, message);
    setSending(false);
    if (ok) setMessage("");
  };

  return (
    <>
      <Textarea
        {...inputStyles}
        placeholder="Short email message"
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        size="sm"
        resize="vertical"
      />
      <Button {...outlineButtonStyles} size="xs" onClick={send} loading={sending}>
        Send
      </Button>
    </>
  );
});

// Edits locally, saves on blur only if the value actually changed.
const InfoTextarea = React.memo(({ agreementId, initialInfo, onSave }) => {
  const [value, setValue] = useState(initialInfo || "");

  useEffect(() => {
    setValue(initialInfo || "");
  }, [initialInfo]);

  return (
    <Textarea
      {...inputStyles}
      value={value}
      placeholder="Update info..."
      size="sm"
      resize="vertical"
      flex="1"
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => {
        if (value !== (initialInfo || "")) onSave(agreementId, value);
      }}
    />
  );
});

const StatCard = ({ label, value, accent }) => (
  <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="lg" boxShadow="sm" px={5} py={4}>
    <Text {...headerStyles} mb={1}>{label}</Text>
    <Text fontFamily="heading" fontWeight="600" fontSize="3xl" lineHeight="1" color={accent || "gray.900"}>
      {value}
    </Text>
  </Box>
);

const AdminDashboard = () => {
  const [agreements, setAgreements] = useState([]);
  const [sortedAgreements, setSortedAgreements] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [file, setFile] = useState(null);
  const [fleet, setFleet] = useState([]);
  const [stats, setStats] = useState(null);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);

  const csrfToken = Cookies.get("csrftoken");
  axios.defaults.headers.common["X-CSRFToken"] = csrfToken;

  useEffect(() => {
    fetchAgreements();
    fetchFleet();
  }, []);

  const fetchAgreements = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/agreements/`, {
        withCredentials: true  // This sends cookies with the request
      });

      setAgreements(response.data.agreements);
      setSortedAgreements(response.data.agreements);
    } catch (error) {
      console.error("Error fetching agreements", error);
    }
  };

  const fetchFleet = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/fleet/`, {
        withCredentials: true,
      });
      setFleet(response.data.fleet);
      setStats(response.data.stats);
    } catch (error) {
      console.error("Error fetching fleet", error);
    }
  };

  const handleRelease = async (instrumentId) => {
    if (!window.confirm(`Release instrument ${instrumentId} back to Available?`)) return;
    try {
      await axios.post(
        `${API_BASE_URL}/api/admin/instruments/${instrumentId}/release/`,
        {},
        { withCredentials: true }
      );
      fetchFleet();
      fetchAgreements();
    } catch (error) {
      console.error("Error releasing instrument", error);
      alert(error.response?.data?.error || "Failed to release the instrument.");
    }
  };

  const sortAgreements = React.useCallback(() => {
    const sorted = [...agreements];
    if (sortConfig.key) {
      sorted.sort((a, b) => {
        let aValue, bValue;

        if (sortConfig.key === "hasActionButton") {
          aValue = ["Created", "Active", "Test", "EndingSoon", "Ended"].includes(a.status) ? 1 : 0;
          bValue = ["Created", "Active", "Test", "EndingSoon", "Ended"].includes(b.status) ? 1 : 0;
        } else {
          aValue = a[sortConfig.key] || "";
          bValue = b[sortConfig.key] || "";
        }

        if (typeof aValue === "string") aValue = aValue.toLowerCase();
        if (typeof bValue === "string") bValue = bValue.toLowerCase();

        if (aValue < bValue) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }
    setSortedAgreements(sorted);
  }, [sortConfig, agreements]);

  useEffect(() => {
    sortAgreements();
  }, [sortAgreements]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handleFileChange = (e) => setFile(e.target.files[0]);
  const handleFileUpload = async () => {
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);

    try {
      await axios.post(`${API_BASE_URL}/api/admin/upload-payments/`, formData, {
        withCredentials: true
      });
      alert("Payments uploaded successfully.");
    } catch (error) {
      console.error("Error uploading file", error);
    }
  };

  const handleSendTestEmail = async () => {
    setSendingTestEmail(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/admin/send-test-email/`,
        {},
        { withCredentials: true }
      );
      const results = response.data.results || {};
      alert(
        Object.entries(results)
          .map(([addr, res]) => `${addr}: ${res}`)
          .join("\n")
      );
    } catch (error) {
      const results = error.response?.data?.results;
      alert(
        results
          ? Object.entries(results).map(([addr, res]) => `${addr}: ${res}`).join("\n")
          : "Failed to send test email."
      );
    } finally {
      setSendingTestEmail(false);
    }
  };

  const handleSendEmail = React.useCallback(async (agreementId, emailMessage) => {
    try {
      await axios.post(
        `${API_BASE_URL}/api/admin/send-email/${agreementId}/`,
        { email_message: emailMessage },
        { withCredentials: true }
      );
      alert("Email sent successfully!");
      return true;
    } catch (error) {
      console.error("Error sending email:", error.response?.data || error);
      alert("Error sending email. Please try again.");
      return false;
    }
  }, []);

  const handleSetSigned = async (agreementId) => {
    try {
      await axios.post(
        `${API_BASE_URL}/api/admin/signed/${agreementId}/`,
        {},  // Empty data object (POST requests expect a body)
        {
          withCredentials: true,
        }
      );
      alert("Contract marked as Active successfully!");
      fetchAgreements();
      fetchFleet();
    } catch (error) {
      console.error("Error updating contract status:", error.response?.data || error);
      alert("Failed to mark the contract as Active. Please try again.");
    }
  };

  const handleSetFinished = async (agreementId) => {
    try {
      await axios.post(`${API_BASE_URL}/api/admin/finished/${agreementId}/`,
        {},  // Empty data object (POST requests expect a body)
        {
          withCredentials: true,
        }
      );
      alert("Contract marked as Finished successfully!");
      fetchAgreements();
      fetchFleet();
    } catch (error) {
      console.error("Error updating contract status:", error.response?.data || error);
      alert("Failed to mark the contract as Finished. Please try again.");
    }
  };

  const handleInfoChange = React.useCallback((agreementId, newInfo) => {
    axios
      .put(
        `${API_BASE_URL}/api/admin/update-info/${agreementId}/`,
        { info: newInfo },  // Request body
        { withCredentials: true }  // Config object
      )
      .then(() => {
        setAgreements((prev) =>
          prev.map((agreement) =>
            agreement.agreementId === agreementId
              ? { ...agreement, info: newInfo }
              : agreement
          )
        );
      })
      .catch((error) => {
        console.error("Error updating info:", error);
      });
  }, []);

  const SortableHeader = ({ sortKey, children }) => (
    <Table.ColumnHeader
      {...headerStyles}
      cursor="pointer"
      onClick={() => handleSort(sortKey)}
      _hover={{ bg: "gray.100", color: "gray.800" }}
      transition="background 0.2s"
    >
      <Flex align="center" gap={1}>
        {children}
        {sortConfig.key === sortKey && (
          sortConfig.direction === "asc" ? <ChevronUp size={12} /> : <ChevronDown size={12} />
        )}
      </Flex>
    </Table.ColumnHeader>
  );

  const isOverdue = (agreement) =>
    agreement.endDate <= todayStr && agreement.status !== "Finished";

  const availabilityCell = (item) => {
    if (item.status === "Available") {
      return <Text color="green.600" fontWeight="semibold">now</Text>;
    }
    if (!item.available_from) {
      return <Text color="gray.400">—</Text>;
    }
    const soon = item.available_from <= in30DaysStr;
    return (
      <Text color={soon ? "orange.600" : "gray.700"} fontWeight={soon ? "semibold" : "normal"}>
        {item.available_from}
      </Text>
    );
  };

  return (
    <Box minH="calc(100vh - 4rem)" bg="gray.50" py={{ base: 6, md: 10 }} px={{ base: 3, md: 6 }}>
      <Box maxW="7xl" mx="auto">
        <Flex align="flex-end" justify="space-between" wrap="wrap" gap={4} mb={6}>
          <Box>
            <Text
              fontFamily="space"
              fontSize="xs"
              letterSpacing="0.3em"
              textTransform="uppercase"
              color="gray.500"
              mb={1}
            >
              akordion.ee · admin
            </Text>
            <Heading fontFamily="heading" fontWeight="500" fontSize="3xl" color="gray.900">
              Dashboard
            </Heading>
          </Box>

          {/* Payments upload */}
          <HStack gap={2}>
            <Input {...inputStyles} type="file" onChange={handleFileChange} size="sm" maxW="280px" pt="3px" />
            <Button {...darkButtonStyles} size="sm" px={5} onClick={handleFileUpload}>
              Upload Payments
            </Button>
            <Button
              {...outlineButtonStyles}
              size="sm"
              px={5}
              onClick={handleSendTestEmail}
              loading={sendingTestEmail}
            >
              Send test e-mail
            </Button>
          </HStack>
        </Flex>

        {/* Summary strip */}
        {stats && (
          <SimpleGrid columns={{ base: 2, md: 4 }} gap={4} mb={6}>
            <StatCard
              label="Utilization"
              value={`${stats.rented} / ${stats.total_instruments}`}
            />
            <StatCard
              label="Returns next 30 days"
              value={stats.returns_next_30_days}
              accent={stats.returns_next_30_days > 0 ? "orange.600" : undefined}
            />
            <StatCard
              label="Outstanding debt"
              value={`${stats.total_debt}€`}
              accent={stats.total_debt > 0 ? "red.500" : "green.600"}
            />
            <StatCard
              label="Invoices stuck (Issued)"
              value={stats.invoices_stuck_issued}
              accent={stats.invoices_stuck_issued > 0 ? "red.500" : "green.600"}
            />
          </SimpleGrid>
        )}

        <Tabs.Root defaultValue="agreements" variant="line" colorPalette="gray">
          <Tabs.List mb={4}>
            {["agreements", "instruments"].map((tab) => (
              <Tabs.Trigger
                key={tab}
                value={tab}
                fontFamily="space"
                fontSize="xs"
                letterSpacing="0.14em"
                textTransform="uppercase"
                color="gray.500"
                _selected={{ color: "gray.900" }}
              >
                {tab}
              </Tabs.Trigger>
            ))}
          </Tabs.List>

          {/* ── Agreements tab ─────────────────────────────────────────── */}
          <Tabs.Content value="agreements">
            <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="lg" boxShadow="sm" overflowX="auto">
              <Table.Root size="sm">
                <Table.Header>
                  <Table.Row bg="gray.50">
                    <SortableHeader sortKey="agreementId">Nr</SortableHeader>
                    <Table.ColumnHeader {...headerStyles}>Name</Table.ColumnHeader>
                    <SortableHeader sortKey="startDate">Start Date</SortableHeader>
                    <SortableHeader sortKey="endDate">End Date</SortableHeader>
                    <SortableHeader sortKey="status">Status</SortableHeader>
                    <SortableHeader sortKey="paymentsDue">Due</SortableHeader>
                    <Table.ColumnHeader {...headerStyles}>Instrument</Table.ColumnHeader>
                    <SortableHeader sortKey="hasActionButton">Actions</SortableHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {sortedAgreements.map((agreement) => (
                    <React.Fragment key={agreement.agreementId}>
                      <Table.Row _hover={{ bg: "gray.50" }} transition="background 0.2s">
                        <Table.Cell fontWeight="semibold" color="gray.900">
                          {agreement.agreementId}
                        </Table.Cell>
                        <Table.Cell>
                          <InfoHover
                            trigger={
                              <VStack align="start" gap={0}>
                                <Text fontWeight="medium" color="gray.900">
                                  {agreement.user.firstName} {agreement.user.lastName}
                                </Text>
                                <Text fontSize="sm" color="gray.500">
                                  {agreement.user.phone}
                                </Text>
                              </VStack>
                            }
                          >
                            <ClientHoverContent user={agreement.user} />
                          </InfoHover>
                        </Table.Cell>
                        <Table.Cell color="gray.700">{agreement.startDate}</Table.Cell>
                        <Table.Cell>
                          <Text
                            color={isOverdue(agreement) ? "red.500" : "gray.700"}
                            fontWeight={isOverdue(agreement) ? "semibold" : "normal"}
                          >
                            {agreement.endDate}
                          </Text>
                        </Table.Cell>
                        <Table.Cell>
                          <StatusBadge palette={AGREEMENT_PALETTE[agreement.status] || "purple"}>
                            {agreement.status}
                          </StatusBadge>
                        </Table.Cell>
                        <Table.Cell>
                          <Text
                            color={agreement.paymentsDue < 0 ? "red.500" : "green.600"}
                            fontWeight="semibold"
                          >
                            {agreement.paymentsDue}€
                          </Text>
                        </Table.Cell>
                        <Table.Cell>
                          <InfoHover
                            trigger={
                              <VStack align="start" gap={0}>
                                <Text fontWeight="medium" color="gray.900">
                                  {agreement.instrument.brand} {agreement.instrument.model}
                                </Text>
                                <Text fontSize="sm" color="gray.500">
                                  ({agreement.instrument.color})
                                </Text>
                              </VStack>
                            }
                          >
                            <InstrumentHoverContent inst={agreement.instrument} />
                          </InfoHover>
                        </Table.Cell>
                        <Table.Cell>
                          <VStack align="stretch" gap={2} minW="180px">
                            {agreement.status === "Created" && (
                              <Button
                                {...darkButtonStyles}
                                size="xs"
                                onClick={() => handleSetSigned(agreement.agreementId)}
                              >
                                Set contract Signed
                              </Button>
                            )}
                            {["Active", "Test", "EndingSoon", "Ended"].includes(agreement.status) && (
                              <Button
                                {...darkButtonStyles}
                                size="xs"
                                onClick={() => handleSetFinished(agreement.agreementId)}
                              >
                                Set Instrument Returned
                              </Button>
                            )}
                            <EmailBox
                              agreementId={agreement.agreementId}
                              onSend={handleSendEmail}
                            />
                          </VStack>
                        </Table.Cell>
                      </Table.Row>
                      <Table.Row bg="gray.50">
                        <Table.Cell colSpan={8} borderBottomWidth="2px" borderColor="gray.200">
                          <HStack align="start" gap={3} py={1}>
                            <Text {...headerStyles} pt={2} minW="40px">Info</Text>
                            <InfoTextarea
                              agreementId={agreement.agreementId}
                              initialInfo={agreement.info}
                              onSave={handleInfoChange}
                            />
                          </HStack>
                        </Table.Cell>
                      </Table.Row>
                    </React.Fragment>
                  ))}
                </Table.Body>
              </Table.Root>
            </Box>
          </Tabs.Content>

          {/* ── Instruments (fleet) tab ────────────────────────────────── */}
          <Tabs.Content value="instruments">
            <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="lg" boxShadow="sm" overflowX="auto">
              <Table.Root size="sm">
                <Table.Header>
                  <Table.Row bg="gray.50">
                    <Table.ColumnHeader {...headerStyles}>Nr</Table.ColumnHeader>
                    <Table.ColumnHeader {...headerStyles}>Instrument</Table.ColumnHeader>
                    <Table.ColumnHeader {...headerStyles}>Status</Table.ColumnHeader>
                    <Table.ColumnHeader {...headerStyles}>Current renter</Table.ColumnHeader>
                    <Table.ColumnHeader {...headerStyles}>Available from</Table.ColumnHeader>
                    <Table.ColumnHeader {...headerStyles}>Idle</Table.ColumnHeader>
                    <Table.ColumnHeader {...headerStyles}>Actions</Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {fleet.map((item) => (
                    <Table.Row key={item.instrumentId} _hover={{ bg: "gray.50" }} transition="background 0.2s">
                      <Table.Cell fontWeight="semibold" color="gray.900">{item.instrumentId}</Table.Cell>
                      <Table.Cell>
                        <InfoHover
                          trigger={
                            <VStack align="start" gap={0}>
                              <Text fontWeight="medium" color="gray.900">
                                {item.brand} {item.model}
                              </Text>
                              <Text fontSize="sm" color="gray.500">({item.color})</Text>
                            </VStack>
                          }
                        >
                          <InstrumentHoverContent inst={item} />
                        </InfoHover>
                      </Table.Cell>
                      <Table.Cell>
                        <StatusBadge palette={INSTRUMENT_PALETTE[item.status] || "gray"}>
                          {item.status}
                        </StatusBadge>
                      </Table.Cell>
                      <Table.Cell>
                        {item.renter ? (
                          <InfoHover
                            trigger={
                              <VStack align="start" gap={0}>
                                <Text fontWeight="medium" color="gray.900">{item.renter.name}</Text>
                                <Text fontSize="sm" color="gray.500">{item.renter.phone}</Text>
                              </VStack>
                            }
                          >
                            <ClientHoverContent user={item.renter} />
                          </InfoHover>
                        ) : (
                          <Text color="gray.400">—</Text>
                        )}
                      </Table.Cell>
                      <Table.Cell>{availabilityCell(item)}</Table.Cell>
                      <Table.Cell>
                        {item.idle_days === null || item.idle_days === undefined ? (
                          <Text color="gray.400">—</Text>
                        ) : (
                          <Text
                            color={item.idle_days > 30 ? "red.500" : "gray.700"}
                            fontWeight={item.idle_days > 30 ? "semibold" : "normal"}
                          >
                            {item.idle_days} days
                          </Text>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        {["Reserved", "AgreementInProgress"].includes(item.status) && (
                          <Button
                            {...outlineButtonStyles}
                            size="xs"
                            color="red.600"
                            borderColor="red.200"
                            _hover={{ bg: "red.50" }}
                            onClick={() => handleRelease(item.instrumentId)}
                          >
                            Release
                          </Button>
                        )}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            </Box>
          </Tabs.Content>
        </Tabs.Root>
      </Box>
    </Box>
  );
};

export default AdminDashboard;
